/**
 * Registration/auth validation — must match Python `utils/auth_utils.py`
 * (validate_password_strength, validate_username_format).
 */

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_SPECIAL_CHARS = '!@#$%^&*()_+-=[]{}|;:,.<>?';
export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 30;

const USERNAME_PATTERN = /^[a-zA-Z0-9_-]+$/;

/** Simple email shape check for UX (server uses Pydantic EmailStr). */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validatePasswordStrength(password: string): { isValid: boolean; issues: string[] } {
  const issues: string[] = [];

  if (password.length < PASSWORD_MIN_LENGTH) {
    issues.push(`Password must be at least ${PASSWORD_MIN_LENGTH} characters long`);
  }
  if (!/[A-Z]/.test(password)) {
    issues.push('Password must contain at least one uppercase letter');
  }
  if (!/[a-z]/.test(password)) {
    issues.push('Password must contain at least one lowercase letter');
  }
  if (!/[0-9]/.test(password)) {
    issues.push('Password must contain at least one digit');
  }
  if (!password.split('').some(char => PASSWORD_SPECIAL_CHARS.includes(char))) {
    issues.push('Password must contain at least one special character');
  }

  return {
    isValid: issues.length === 0,
    issues,
  };
}

export function validateUsername(username: string): { isValid: boolean; issues: string[] } {
  const issues: string[] = [];
  if (username == null) {
    issues.push('Username must be at least 3 characters long');
    return { isValid: false, issues };
  }
  const s = username.trim();
  if (!s) {
    issues.push('Username must be at least 3 characters long');
  } else if (s.length < USERNAME_MIN_LENGTH) {
    issues.push('Username must be at least 3 characters long');
  } else if (s.length > USERNAME_MAX_LENGTH) {
    issues.push('Username must be no more than 30 characters long');
  } else if (!USERNAME_PATTERN.test(s)) {
    issues.push('Username can only contain letters, numbers, underscores, and hyphens');
  }
  return {
    isValid: issues.length === 0,
    issues,
  };
}

export function validateEmail(email: string): boolean {
  return EMAIL_PATTERN.test(email.trim());
}

/** Counts how many password-strength criteria are met (0–5) for strength meters. */
export function countPasswordStrengthCriteria(password: string): number {
  let n = 0;
  if (password.length >= PASSWORD_MIN_LENGTH) n += 1;
  if (/[a-z]/.test(password)) n += 1;
  if (/[A-Z]/.test(password)) n += 1;
  if (/[0-9]/.test(password)) n += 1;
  if (password.split('').some(char => PASSWORD_SPECIAL_CHARS.includes(char))) n += 1;
  return n;
}
